/*
 * Copyright (c) 2025, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {VisuallyHidden} from '@salesforce/retail-react-app/app/components/shared/ui'
import CartQuantityPicker from '../cart-quantity-picker'

const ProductQuantityPicker = ({
    product,
    onItemQuantityChange,
    stepQuantity,
    quantity,
    setQuantity
}) => {
    const intl = useIntl()
    const maxLimit = product?.c_orderLimit || product?.inventory?.stockLevel
    const handleQuantityChange = (stringValue, numberValue) => {
        const cartDivs = document.querySelectorAll('.sf-cart-item')
        const uuid = product.itemId
        const index = [...cartDivs].findIndex((el) => el.dataset.uuid === uuid) // NOTE: We are getting index like this because of hidden CB metals and stones.
        window.updatedIndex = index + 1
        // Set the Quantity of product to value of input if value number
        if (numberValue >= 0) {
            // Call handler
            const newValue = numberValue > maxLimit ? maxLimit : numberValue
            onItemQuantityChange(newValue).then(
                (isValidChange) => isValidChange && setQuantity(newValue)
            )
        } else if (stringValue === '') {
            // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
            // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
            setQuantity(stringValue)
        }
    }

    const handleQuantityBlur = (e) => {
        // Default to last known quantity if a user leaves the box with an invalid value
        const {value} = e.target

        if (!value) {
            setQuantity(product.quantity)
        }
    }

    return (
        <>
            <CartQuantityPicker
                step={stepQuantity}
                value={quantity}
                min={0}
                clampValueOnBlur={false}
                onBlur={handleQuantityBlur}
                onChange={handleQuantityChange}
                productName={product?.name}
                orderLimit={maxLimit}
            />
            <VisuallyHidden role="status">
                {product?.name}
                {intl.formatMessage(
                    {
                        id: 'item_variant.assistive_msg.quantity',
                        defaultMessage: 'Quantity {quantity}'
                    },
                    {
                        quantity: product?.quantity
                    }
                )}
            </VisuallyHidden>
        </>
    )
}

ProductQuantityPicker.propTypes = {
    product: PropTypes.object.isRequired,
    onItemQuantityChange: PropTypes.func.isRequired,
    stepQuantity: PropTypes.number.isRequired,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    setQuantity: PropTypes.func.isRequired
}

export default ProductQuantityPicker
